import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { subscribeMobile, formatApiError } from "../lib/api";
import { metaTrack } from "../analytics/metaPixel";

export const SubscribePopup = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (localStorage.getItem("rvi_popup_done")) return;
    if (pathname.startsWith("/admin") || pathname.startsWith("/checkout")) return;
    const t = setTimeout(() => setOpen(true), 4000);
    return () => clearTimeout(t);
  }, [pathname]);

  const close = () => {
    localStorage.setItem("rvi_popup_done", "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone.trim())) return toast.error("Please enter a valid 10-digit Indian mobile number");
    if (!consent) return toast.error("Please agree to receive updates to continue");
    setLoading(true);
    try {
      const res = await subscribeMobile(phone.trim());
      metaTrack("Lead", { content_name: "Mobile subscription" });
      metaTrack("CompleteRegistration", { status: "subscribed" });
      if (res.duplicate) toast.info("This number is already subscribed to our updates");
      setDone(true);
      localStorage.setItem("rvi_popup_done", "1");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="bg-ivory border-gold/40 rounded-sm max-w-2xl p-0 overflow-hidden" data-testid="subscribe-popup">
        <DialogTitle className="sr-only">Subscribe for Ilkal saree updates</DialogTitle>
        <div className="grid sm:grid-cols-[0.85fr_1.15fr]">
          <div className="hidden sm:block relative">
            <img src="/hero-ilkal.jpg" alt="Handloom Ilkal saree by R V Ilkal Sarees" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-maroon/60 to-transparent" />
            <p className="absolute bottom-5 left-5 right-5 text-ivory font-heading text-lg leading-snug">Handwoven in Ilkal, Karnataka</p>
          </div>
          <div className="p-7 sm:p-9 relative">
            <button onClick={close} aria-label="Close popup" data-testid="popup-close-btn" className="absolute top-4 right-4 text-charcoal/40 hover:text-maroon">
              <X className="w-5 h-5" />
            </button>
            {done ? (
              <div className="py-10 text-center" data-testid="popup-success">
                <CheckCircle2 className="w-12 h-12 text-maroon mx-auto" strokeWidth={1.25} />
                <h3 className="font-heading text-2xl text-charcoal mt-4">You're in the Ilkal family</h3>
                <p className="text-sm text-charcoal/60 mt-2 leading-relaxed">New arrivals, festival offers and exclusive saree drops will reach you first.</p>
                <button onClick={close} data-testid="popup-done-btn" className="mt-6 bg-maroon text-ivory text-xs tracking-[0.18em] uppercase px-8 py-3.5 hover:bg-maroon-dark transition-colors">
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold">R V Ilkal Sarees</p>
                <h3 className="font-heading text-2xl sm:text-3xl text-charcoal mt-2 leading-tight">Join the Ilkal Family</h3>
                <p className="text-sm text-charcoal/60 mt-3 leading-relaxed">
                  Be first to know about new handloom Ilkal saree arrivals, festival offers and members-only collections — straight to your phone.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="subscribe-form">
                  <div className="flex items-center border border-gold/40 focus-within:border-gold bg-ivory">
                    <span className="pl-4 text-charcoal/40 flex items-center gap-1.5 text-sm">
                      <Phone className="w-4 h-4" strokeWidth={1.5} /> +91
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      placeholder="10-digit mobile number"
                      data-testid="popup-phone-input"
                      className="flex-1 min-w-0 bg-transparent px-3 py-3.5 text-sm outline-none text-charcoal placeholder:text-charcoal/40"
                    />
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="popup-consent">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#6B1C23]" />
                    <span className="text-[11px] text-charcoal/55 leading-relaxed">
                      I agree to receive offers, order updates and marketing messages on WhatsApp/SMS from R V Ilkal Sarees. I can opt out anytime.
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="popup-subscribe-btn"
                    className="w-full bg-maroon text-ivory text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon-dark transition-colors disabled:opacity-60"
                  >
                    {loading ? "Subscribing…" : "Get Updates"}
                  </button>
                  <p className="text-[10px] text-charcoal/40 text-center">We respect your privacy. Your number is stored securely and never shared.</p>
                </form>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
